import { ActionFunctionArgs } from "@remix-run/node";
import { useFetcher } from "@remix-run/react";
import { assert, jauntPost, Project, RunningTimer } from "~/lib";
import ProjectItem from "./ProjectItem";
import RunningTimerItem from "./RunningTimerItem";
import { ProjectsLoader } from "~/routes/api.projects/route";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";

/**
 * Create a project
 */
export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const name = formData.get("name");

  assert(typeof name === "string" && name, "Invalid name");

  const response = await jauntPost("v0/projects", request, {
    name,
  });

  assert(response.ok, "Error occurred when creating project", 500);
  return null;
};

export default function projects() {
  const fetcher = useFetcher();
  const dataFetcher = useFetcher<ProjectsLoader>();
  const greeterFetcher = useFetcher<string>();
  const [runningTimer, setRunningTimer] = useState<RunningTimer | undefined>(
    undefined,
  );
  const [projects, setProjects] = useState<Project[]>([]);
  const [greetResponse, setGreetResponse] = useState<string>("");

  const loadData = () => {
    if (dataFetcher.state !== "idle") {
      return;
    }

    const now = DateTime.now();

    let cutoffTimeToday = now.set({
      hour: 5,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    const cutoff: number = (
      cutoffTimeToday >= now
        ? cutoffTimeToday.minus({ day: 1 })
        : cutoffTimeToday
    ).toUnixInteger();

    dataFetcher.load(`/api/projects?since=${cutoff}`);
  };

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Poll for updates
  useEffect(() => {
    const updateTimer = () => {
      loadData();
    };

    const intervalId = setInterval(updateTimer, 10000);
    return () => clearInterval(intervalId);
  }, []);

  // Set data from load
  useEffect(() => {
    if (dataFetcher.data) {
      setRunningTimer(dataFetcher.data.runningTimer);
      setProjects(dataFetcher.data.projects);
    }
  }, [dataFetcher.data]);

  const runningProject = projects.find(
    (project) => project.id === runningTimer?.projectId,
  );

  useEffect(() => {
    const action = () => {
      loadData();
    };
    addEventListener("focus", action);
    return () => removeEventListener("focus", action);
  }, []);

  useEffect(() => {
      if (greeterFetcher.state === "idle" && greeterFetcher.data) {
          setGreetResponse(greeterFetcher.data);
      }
  }, [greeterFetcher.data]);

  const greet = () => {
    greeterFetcher.load("/api/greet");
  };

  return (
    <div>
      <RunningTimerItem project={runningProject} runningTimer={runningTimer} />

      <button onClick={greet}>Greet</button>
      {greetResponse && <span>{greetResponse}</span>}
      <h1>Projects</h1>
      {projects.map((project) => (
        <ProjectItem
          key={project.id}
          project={project}
          runningTimer={runningTimer}
        />
      ))}
      <fetcher.Form method="post">
        <input defaultValue={""} name="name" placeholder="Name" type="text" />
        <button type="submit">Create Project</button>
      </fetcher.Form>
    </div>
  );
}
